import * as THREE from 'three';
import {
  canEstablishParentChild,
  enforceHotspotHierarchyRules,
  isHotspotAssetRecord,
  isHotspotObject,
} from '../../../../../utils/hotspotHierarchyUtils';
import { resolveAssetParentId } from '../../../../GridAssets/shapeAssetUtils.js';

export const useHierarchyUtils = ({
  scene,
  assets_data,
  light_data,
  camera_properties,
  particles,
  allAssets,
}) => {
  const getAssetId = (asset) => asset?._id || asset?.url || asset?.name;

  const getObjectByAsset = (asset) => {
    const id = getAssetId(asset);
    if (!id) return null;
    let obj = scene?.getObjectByName(id);
    if (!obj && asset?.asset_file_name) {
      obj = scene?.getObjectByName(asset.asset_file_name);
    }
    // If still not found and this might be a text object, search more thoroughly
    if (!obj) {
      scene?.traverse((child) => {
        if (
          !obj &&
          (child.name === id ||
            child._id === id ||
            child.url === id ||
            (child.deleteType === 'text' &&
              (child.userData?.text_material?.id === id ||
                child.userData?.id === id)))
        ) {
          obj = child;
        }
      });
    }
    return obj;
  };

  const findObjectInScene = (identifier) => findObjectInSceneStandalone(scene, identifier);

  const reconstructParentChildRelationships = (
    scene,
    assets_data,
    light_data,
    camera_properties,
    latestParticles
  ) => reconstructParentChildRelationshipsStandalone(
    scene,
    assets_data,
    light_data,
    camera_properties,
    latestParticles || particles
  );

  const findDirectChildrenAssets = (parentObj, visited = new Set()) => {
    const result = [];

    if (!parentObj) return result;

    if (isHotspotObject(parentObj)) {
      return result;
    }

    // Get the parent identifier and check for circular references
    const parentId =
      parentObj.name ||
      parentObj._id ||
      parentObj.id ||
      parentObj.temp_file_name;
    if (visited.has(parentId)) {
      return result;
    }
    visited.add(parentId);

    // Check regular assets for parent-child relationships
    const processedItems = new Set(); // by itemId
    const processedObjectUuids = new Set(); // by scene object to avoid duplicate entries for same object

    allAssets.forEach((asset) => {
      // Add null checks for asset structure
      if (!asset || !asset.item || !asset.type) {
        console.warn(
          'Invalid asset structure in findDirectChildrenAssets:',
          asset
        );
        return;
      }

      const { item, type } = asset;
      if (isHotspotAssetRecord(item)) {
        return;
      }
      const itemId = item.name || item._id || item.id;
      if (processedItems.has(itemId)) {
        return;
      }

      const obj = getObjectByAsset(item);
      if (obj && processedObjectUuids.has(obj.uuid)) {
        return;
      }

      // First check Three.js scene hierarchy
      if (obj && obj.parent && parentObj && obj.parent === parentObj) {
        if (isHotspotObject(obj)) {
          return;
        }
        result.push({ item, type });
        processedItems.add(itemId);
        if (obj.uuid) processedObjectUuids.add(obj.uuid);
      }
      // Then check asset data for parent-child relationships (only if not already added by scene)
      else if (item.parent) {
        const itemParentId =
          typeof item.parent === 'object'
            ? item.parent.temp_file_name ||
            item.parent._id ||
            item.parent.id ||
            item.parent.name
            : item.parent;

        const parentObjId =
          parentObj.temp_file_name ||
          parentObj.name ||
          parentObj._id ||
          parentObj.id;

        if (itemParentId === parentObjId) {
          result.push({ item, type });
          processedItems.add(itemId);
          if (obj && obj.uuid) processedObjectUuids.add(obj.uuid);
        }
      }
    });

    // Check lights for parent-child relationships
    light_data.forEach((light) => {
      const lightObj = scene.getObjectByName(light.id);

      // Check Three.js scene hierarchy first (most reliable)
      if (lightObj && lightObj.parent === parentObj) {
        result.push({
          item: { _id: light.id, name: light.name, file_name: light.name },
          type: 'light',
        });
      }
      // Only check Redux state if Three.js hierarchy doesn't match
      // This prevents duplication when both conditions are true
      else if (light.parent && !lightObj) {
        const lightParentId =
          typeof light.parent === 'object'
            ? light.parent.temp_file_name ||
            light.parent._id ||
            light.parent.id ||
            light.parent.name
            : light.parent;

        const parentObjId =
          parentObj.temp_file_name ||
          parentObj.name ||
          parentObj._id ||
          parentObj.id;

        if (lightParentId === parentObjId) {
          result.push({
            item: { _id: light.id, name: light.name, file_name: light.name },
            type: 'light',
          });
        }
      }
    });

    // Check particles for parent-child relationships
    const addedParticleIds = new Set();

    particles.forEach((particle) => {
      const particleId = particle.id || particle._id || particle.name;

      // Try multiple ways to find the particle in scene
      const particleObj =
        scene.getObjectByName(particle.id) ||
        scene.getObjectByName(particle._id) ||
        scene.getObjectByName(particle.name);



      if (particleObj && particleObj.parent === parentObj) {
        if (!addedParticleIds.has(particleId)) {
          addedParticleIds.add(particleId);
          result.push({
            item: {
              _id: particle.id,
              name: particle.name,
              file_name: particle.name,
            },
            type: 'particle',
          });
        }
      } else if (particle.parent) {
        if (addedParticleIds.has(particleId)) return;

        const particleParentId =
          typeof particle.parent === 'object'
            ? particle.parent.temp_file_name ||
            particle.parent._id ||
            particle.parent.id ||
            particle.parent.name
            : particle.parent;

        const parentObjId =
          parentObj.temp_file_name ||
          parentObj.name ||
          parentObj._id ||
          parentObj.id;

        if (particleParentId === parentObjId) {
          if (!addedParticleIds.has(particleId)) {
            addedParticleIds.add(particleId);
            result.push({
              item: {
                _id: particle.id,
                name: particle.name,
                file_name: particle.name,
              },
              type: 'particle',
            });
          }
        }
      }
    });

    // Check for camera children
    if (
      parentObj &&
      Array.isArray(parentObj.children) &&
      parentObj.children.length > 0
    ) {
      const seen = new Set();
      parentObj.children.forEach((child) => {
        if (
          (child.type === 'PerspectiveCamera' ||
            child.type === 'OrthographicCamera') &&
          !result.some(
            ({ item }) =>
              item && (item._id === child.name || item.name === child.name)
          ) &&
          !seen.has(child.name)
        ) {
          seen.add(child.name);
          result.push({
            item: {
              _id: child.name || 'DemoCamera',
              name: 'Camera',
              file_name: 'Camera',
              type: 'camera',
            },
            type: 'camera',
          });
        }
      });
    }

    return result;
  };

  const hasChildren = (obj, visited = new Set()) => {
    if (!obj) return false;
    // After reconstruction, check actual Three.js children
    const directChildren = findDirectChildrenAssets(obj, visited);

    return directChildren.length > 0;
  };

  const renderChildren = (parentObj, level = 1, renderChildComponent) => {
    const children = findDirectChildrenAssets(parentObj);
    if (!children.length) return null;

    return children.map(({ item, type }, idx) =>
      renderChildComponent({ item, type, parentObj, level, idx })
    );
  };

  const deleteAllChildren = (
    parentObj,
    scene,
    dispatch,
    assets_data,
    light_data,
    particles
  ) => {
    if (!parentObj) {
      console.warn('deleteAllChildren: parentObj is null or undefined');
      return;
    }

    const parentId =
      parentObj.name ||
      parentObj._id ||
      parentObj.id ||
      parentObj.temp_file_name;
    if (!parentId) {
      console.warn(
        'deleteAllChildren: No valid parentId found for parentObj:',
        parentObj
      );
      return;
    }

    const childrenToDelete = [];

    // Find all children in Redux state
    const allAssets = [];

    // Add regular assets
    if (assets_data) {
      ['image', 'mesh', 'video', 'audio', 'text', 'hotspot'].forEach((type) => {
        if (assets_data[type]) {
          assets_data[type].forEach((asset) => {
            allAssets.push({ ...asset, type });
          });
        }
      });
    }

    // Add lights
    if (light_data) {
      light_data.forEach((light) => {
        allAssets.push({
          ...light,
          type: 'light',
          _id: light.temp_file_name || light.id || light._id,
          name: light.name || light.temp_file_name,
        });
      });
    }

    // Add particles
    if (particles) {
      particles.forEach((particle) => {
        allAssets.push({
          ...particle,
          type: 'particle',
          _id: particle.id,
          name: particle.name,
        });
      });
    }

    // Find all children recursively
    const findChildrenRecursively = (currentParentId) => {
      allAssets.forEach((asset) => {
        // Add null checks for asset structure
        if (!asset || !asset.item || !asset.type) {
          console.warn('Invalid asset structure in deleteAllChildren:', asset);
          return;
        }

        const { item, type } = asset;

        // Check if item has parent property and it's not null/undefined
        if (!item.parent) {
          return;
        }

        const itemParentId =
          typeof item.parent === 'object'
            ? item.parent.temp_file_name ||
            item.parent._id ||
            item.parent.id ||
            item.parent.name
            : item.parent;

        if (itemParentId === currentParentId) {
          childrenToDelete.push({ item, type });
          // Recursively find children of this child
          const childId = item._id || item.id || item.name;
          if (childId) {
            findChildrenRecursively(childId);
          }
        }
      });
    };

    findChildrenRecursively(parentId);

    // Delete all children from Redux state
    childrenToDelete.forEach((childToDelete) => {
      // Add null checks for childToDelete structure
      if (!childToDelete || !childToDelete.item || !childToDelete.type) {
        console.warn('Invalid childToDelete structure:', childToDelete);
        return;
      }

      const { item, type } = childToDelete;
      const itemId = item._id || item.id || item.name;

      if (!itemId) {
        console.warn('No valid ID found for child to delete:', item);
        return;
      }

      try {
        if (type === 'light') {
          dispatch({
            type: 'data/removeLight',
            payload: itemId,
          });
        } else if (type === 'particle') {
          dispatch({
            type: 'particles/removeParticle',
            payload: itemId,
          });
        } else if (['image', 'mesh', 'video', 'audio', 'text', 'hotspot'].includes(type)) {
          const updatedAssets = assets_data[type]?.filter((asset) => {
            const assetId = asset._id || asset?.url || asset?.name;
            return assetId !== itemId;
          });
          if (updatedAssets) {
            dispatch({
              type: 'data/update_assets_data',
              payload: { type: type, data: updatedAssets },
            });
          }
        }
      } catch (error) {
        console.error('Error deleting child from Redux state:', error, {
          item,
          type,
          itemId,
        });
      }
    });

    // Remove children from Three.js scene
    childrenToDelete.forEach((childToDelete) => {
      // Add null checks for childToDelete structure
      if (!childToDelete || !childToDelete.item) {
        console.warn(
          'Invalid childToDelete structure for scene removal:',
          childToDelete
        );
        return;
      }

      const { item } = childToDelete;
      const itemId = item._id || item.id || item.name;

      if (!itemId) {
        console.warn('No valid ID found for scene removal:', item);
        return;
      }

      try {
        const sceneObj = scene.getObjectByName(itemId);
        if (sceneObj && sceneObj.parent) {
          safeRemoveFromParent(sceneObj);
        }
      } catch (error) {
        console.error('Error removing child from Three.js scene:', error, {
          item,
          itemId,
        });
      }
    });

    // Clean up parent references in remaining assets
    const cleanupParentReferences = () => {
      const allRemainingAssets = [];

      // Collect all remaining assets after deletion
      if (assets_data) {
        ['image', 'mesh', 'video', 'audio', 'text', 'hotspot'].forEach((type) => {
          if (assets_data[type]) {
            assets_data[type].forEach((asset) => {
              if (asset && typeof asset === 'object') {
                allRemainingAssets.push({ ...asset, type });
              } else {
                console.warn(
                  'Invalid asset in assets_data:',
                  asset,
                  'type:',
                  type
                );
              }
            });
          }
        });
      }

      if (light_data) {
        light_data.forEach((light) => {
          if (light && typeof light === 'object') {
            allRemainingAssets.push({
              ...light,
              type: 'light',
              _id: light.temp_file_name || light.id || light._id,
              name: light.name || light.temp_file_name,
            });
          } else {
            console.warn('Invalid light in light_data:', light);
          }
        });
      }

      if (particles) {
        particles.forEach((particle) => {
          if (particle && typeof particle === 'object') {
            allRemainingAssets.push({
              ...particle,
              type: 'particle',
              _id: particle.id,
              name: particle.name,
            });
          } else {
            console.warn('Invalid particle in particles:', particle);
          }
        });
      }

      // Find assets that reference the deleted parent or any of its children
      const assetsToUpdate = [];
      const deletedIds = new Set([
        parentId,
        ...childrenToDelete.map(
          (child) => child.item._id || child.item.id || child.item.name
        ),
      ]);

      allRemainingAssets.forEach((asset) => {
        // Add null checks for asset structure
        if (!asset || !asset.item || !asset.type) {
          console.warn(
            'Invalid asset structure in cleanupParentReferences:',
            asset
          );
          return;
        }

        const { item, type } = asset;

        // Check if item has parent property and it's not null/undefined
        if (!item.parent) {
          return;
        }

        const itemParentId =
          typeof item.parent === 'object'
            ? item.parent.temp_file_name ||
            item.parent._id ||
            item.parent.id ||
            item.parent.name
            : item.parent;

        if (deletedIds.has(itemParentId)) {
          assetsToUpdate.push({ item, type, newParent: null });
        }
      });

      // Update assets to remove parent references
      assetsToUpdate.forEach((assetToUpdate) => {
        // Add null checks for assetToUpdate structure
        if (!assetToUpdate || !assetToUpdate.item || !assetToUpdate.type) {
          console.warn(
            'Invalid assetToUpdate structure in cleanupParentReferences:',
            assetToUpdate
          );
          return;
        }

        const { item, type, newParent } = assetToUpdate;
        const itemId = item._id || item.id || item.name;

        try {
          if (type === 'light') {
            const updatedLights = light_data.map((light) => {
              if (light.id === itemId || light._id === itemId) {
                return { ...light, parent: newParent };
              }
              return light;
            });
            dispatch({
              type: 'data/lightDataUpdate',
              payload: updatedLights,
            });
          } else if (type === 'particle') {
            dispatch({
              type: 'particles/updateParticle',
              payload: { id: itemId, updates: { parent: newParent } },
            });
          } else if (
            ['image', 'mesh', 'video', 'audio', 'text', 'hotspot'].includes(type)
          ) {
            const updatedAssets = assets_data[type]?.map((asset) => {
              const assetId = asset._id || asset?.url || asset?.name;
              if (assetId === itemId) {
                return { ...asset, parent: newParent };
              }
              return asset;
            });
            if (updatedAssets) {
              dispatch({
                type: 'data/update_assets_data',
                payload: { type: type, data: updatedAssets },
              });
            }
          }
        } catch (error) {
          console.error('Error updating parent reference:', error, {
            item,
            type,
            itemId,
          });
        }
      });
    };

    // Clean up parent references
    cleanupParentReferences();

    return childrenToDelete;
  };

  const cleanupParentReferences = (
    deletedIds,
    scene,
    dispatch,
    assets_data,
    light_data,
    particles
  ) => {
    if (!deletedIds || deletedIds.length === 0) return;

    const deletedIdSet = new Set(deletedIds);
    const allRemainingAssets = [];

    // Collect all remaining assets
    if (assets_data) {
      ['image', 'mesh', 'video', 'audio', 'text', 'hotspot'].forEach((type) => {
        if (assets_data[type]) {
          assets_data[type].forEach((asset) => {
            if (asset && typeof asset === 'object') {
              allRemainingAssets.push({ ...asset, type });
            } else {
              console.warn(
                'Invalid asset in assets_data:',
                asset,
                'type:',
                type
              );
            }
          });
        }
      });
    }

    if (light_data) {
      light_data.forEach((light) => {
        if (light && typeof light === 'object') {
          allRemainingAssets.push({
            ...light,
            type: 'light',
            _id: light.temp_file_name || light.id || light._id,
            name: light.name || light.temp_file_name,
          });
        } else {
          console.warn('Invalid light in light_data:', light);
        }
      });
    }

    if (particles) {
      particles.forEach((particle) => {
        if (particle && typeof particle === 'object') {
          allRemainingAssets.push({
            ...particle,
            type: 'particle',
            _id: particle.id,
            name: particle.name,
          });
        } else {
          console.warn('Invalid particle in particles:', particle);
        }
      });
    }

    // Find assets that reference the deleted objects
    const assetsToUpdate = [];

    allRemainingAssets.forEach((asset) => {
      // Add null checks for asset structure
      if (!asset || !asset.item || !asset.type) {
        console.warn(
          'Invalid asset structure in cleanupParentReferences:',
          asset
        );
        return;
      }

      const { item, type } = asset;

      // Check if item has parent property and it's not null/undefined
      if (!item.parent) {
        return;
      }

      const itemParentId =
        typeof item.parent === 'object'
          ? item.parent.temp_file_name ||
          item.parent._id ||
          item.parent.id ||
          item.parent.name
          : item.parent;

      if (deletedIdSet.has(itemParentId)) {
        assetsToUpdate.push({ item, type, newParent: null });
      }
    });

    // Update assets to remove parent references
    assetsToUpdate.forEach((assetToUpdate) => {
      // Add null checks for assetToUpdate structure
      if (!assetToUpdate || !assetToUpdate.item || !assetToUpdate.type) {
        console.warn(
          'Invalid assetToUpdate structure in cleanupParentReferences:',
          assetToUpdate
        );
        return;
      }

      const { item, type, newParent } = assetToUpdate;
      const itemId = item._id || item.id || item.name;

      if (!itemId) {
        console.warn('No valid ID found for parent reference update:', item);
        return;
      }

      try {
        if (type === 'light') {
          const updatedLights = light_data.map((light) => {
            if (light.id === itemId || light._id === itemId) {
              return { ...light, parent: newParent };
            }
            return light;
          });
          dispatch({
            type: 'data/lightDataUpdate',
            payload: updatedLights,
          });
        } else if (type === 'particle') {
          dispatch({
            type: 'particles/updateParticle',
            payload: { id: itemId, updates: { parent: newParent } },
          });
        } else if (['image', 'mesh', 'video', 'audio', 'text', 'hotspot'].includes(type)) {
          const updatedAssets = assets_data[type]?.map((asset) => {
            const assetId = asset._id || asset?.url || asset?.name;
            if (assetId === itemId) {
              return { ...asset, parent: newParent };
            }
            return asset;
          });
          if (updatedAssets) {
            dispatch({
              type: 'data/update_assets_data',
              payload: { type: type, data: updatedAssets },
            });
          }
        }
      } catch (error) {
        console.error('Error updating parent reference:', error, {
          item,
          type,
          itemId,
        });
      }
    });

    return assetsToUpdate.length;
  };

  return {
    getAssetId,
    getObjectByAsset,
    findObjectInScene,
    reconstructParentChildRelationships,
    findDirectChildrenAssets,
    hasChildren,
    renderChildren,
    deleteAllChildren,
    cleanupParentReferences,
  };
};

export const findObjectInScene = (scene, identifier) => {
  if (!identifier || !scene) return null;
  // Try direct name lookup first
  let obj = scene.getObjectByName(identifier);

  if (!obj) {
    scene.traverse((child) => {
      if (child.temp_file_name == identifier) {
        obj = child;
        return;
      }
    });
  }
  if (obj) return obj;

  const idStem =
    typeof identifier === 'string'
      ? identifier.replace(/\.[^/.]+$/, '')
      : identifier;

  // Try traversing scene for various matches
  scene.traverse((child) => {
    const fileStem = child.file_name
      ? child.file_name.replace(/\.[^/.]+$/, '')
      : null;
    if (
      !obj &&
      (child.name === identifier ||
        child._id === identifier ||
        child.url === identifier ||
        child.file_name === identifier ||
        child.asset_file_name === identifier ||
        (fileStem && (fileStem === identifier || fileStem === idStem)) ||
        child.userData?.id === identifier ||
        child.userData?._id === identifier ||
        child.userData?.temp_file_name === identifier ||
        child.userData?.file_name === identifier ||
        child.userData?.material_settings?.id === identifier ||
        (child.userData?.isHotspot &&
          (child.userData?.hotspot?.id === identifier ||
            child.name === identifier)) ||
        // Text-specific identifiers
        (child.deleteType === 'text' &&
          (child.userData?.text_material?.id === identifier ||
            child.userData?.text_material?._id === identifier)))
    ) {
      obj = child;
    }
  });

  return obj;
};

const findObjectInSceneStandalone = findObjectInScene;

/**
 * Safely remove a child from its parent to avoid splice errors when
 * parent.children is frozen, a proxy, or otherwise non-standard.
 */
export const safeRemoveFromParent = (childObj) => {
  const parent = childObj?.parent;
  if (!parent) return;
  try {
    parent.remove(childObj);
  } catch (err) {
    const children = parent.children;
    if (Array.isArray(children) && !Object.isFrozen(children)) {
      const index = children.indexOf(childObj);
      if (index !== -1) {
        childObj.parent = null;
        children.splice(index, 1);
      }
    } else {
      childObj.parent = null;
    }
  }
};

const readNum = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

/** Read LOCAL transform stored on publish (position/rotation in radians after load path). */
const readAssetLocalTransform = (item) => {
  if (!item) return null;

  const hasPosition =
    item.position_x != null ||
    item.position_y != null ||
    item.position_z != null ||
    item.position?.x != null ||
    item.position?.y != null ||
    item.position?.z != null;

  if (!hasPosition) return null;

  const rotationUsesDegrees =
    item.type === 'mesh' ||
    item.deleteType === 'mesh' ||
    item.glb_type === 'shapes' ||
    item.glb_type === 'normal' ||
    item.glb_type === 'fbx' ||
    item.glb_type === 'obj' ||
    item.type === 'image' ||
    item.deleteType === 'image';

  const toRad = (value) =>
    rotationUsesDegrees
      ? THREE.MathUtils.degToRad(readNum(value, 0))
      : readNum(value, 0);

  return {
    position: new THREE.Vector3(
      readNum(item.position_x ?? item.position?.x, 0),
      readNum(item.position_y ?? item.position?.y, 0),
      readNum(item.position_z ?? item.position?.z, 0)
    ),
    rotation: new THREE.Euler(
      toRad(item.rotation_x ?? item.rotation?.x),
      toRad(item.rotation_y ?? item.rotation?.y),
      toRad(item.rotation_z ?? item.rotation?.z),
      'XYZ'
    ),
    scale: new THREE.Vector3(
      Math.max(readNum(item.scale_x ?? item.scale?.x, 1), 0.000001),
      Math.max(readNum(item.scale_y ?? item.scale?.y, 1), 0.000001),
      Math.max(readNum(item.scale_z ?? item.scale?.z, 1), 0.000001)
    ),
  };
};

const registerAssetLookupKeys = (map, item) => {
  if (!item) return;
  [
    item.id,
    item._id,
    item.name,
    item.temp_file_name,
    item.file_name,
    item.asset_file_name,
    item.url,
    item.text_material?.id,
    item.text_material?._id,
    item.file_name?.replace(/\.[^/.]+$/, ''),
    item.asset_file_name?.replace(/\.[^/.]+$/, ''),
  ]
    .filter(Boolean)
    .forEach((key) => {
      if (!map.has(key)) map.set(key, item);
    });
};

const applyStoredLocalTransform = (childObj, childAsset) => {
  const storedLocal = readAssetLocalTransform(childAsset);
  if (!storedLocal) return false;
  childObj.position.copy(storedLocal.position);
  childObj.rotation.copy(storedLocal.rotation);
  if (childObj.deleteType === 'image') {
    const planeW = childObj.geometry?.parameters?.width || 1;
    const planeH = childObj.geometry?.parameters?.height || 1;
    childObj.scale.set(
      storedLocal.scale.x / planeW,
      storedLocal.scale.y / planeH,
      storedLocal.scale.z
    );
  } else {
    childObj.scale.copy(storedLocal.scale);
  }
  childObj.updateMatrixWorld(true);
  return true;
};

export const reconstructParentChildRelationships = (
  scene,
  assets_data,
  light_data,
  camera_properties,
  particles
) => {
  if (!scene) return;

  // Create parent-child mapping from database data
  const childParentMap = new Map();
  const childAssetByKey = new Map();
  const allAssets = [];

  // Add regular assets
  if (assets_data) {
    ['image', 'mesh', 'video', 'audio', 'text', 'hotspot', 'empty'].forEach(
      (type) => {
        if (assets_data[type]) {
          assets_data[type].forEach((asset) => {
            allAssets.push({ ...asset, type });
          });
        }
      }
    );
  }

  // Add cameras
  if (camera_properties) {
    camera_properties.forEach((camera) => {
      allAssets.push({
        ...camera,
        type: 'camera',
        _id: camera._id || camera.id,
        name: camera.name || 'Camera',
      });
    });
  }

  // Add lights
  if (light_data) {
    light_data.forEach((light) => {
      allAssets.push({
        ...light,
        type: 'light',
        _id: light.temp_file_name || light.id || light._id,
        name: light.name || light.temp_file_name,
      });
    });
  }

  if (particles) {
    particles.forEach((particle) => {
      allAssets.push({
        ...particle,
        type: 'particle',
        _id: particle.id,
        name: particle.name,
      });
    });
  }

  allAssets.forEach((item) => {
    registerAssetLookupKeys(childAssetByKey, item);
    if (item.parent && !isHotspotAssetRecord(item)) {
      const childKey =
        item.id ||
        item._id ||
        item.name ||
        item.temp_file_name ||
        item.text_material?.id ||
        item.text_material?._id;
      if (childKey) {
        childParentMap.set(childKey, item.parent);
      }
    }
  });

  // Reconstruct the relationships in Three.js scene
  childParentMap.forEach((parentId, childId) => {
    const resolvedChildId = resolveAssetParentId(childId, allAssets);
    const resolvedParentId = resolveAssetParentId(parentId, allAssets);
    const childObj =
      findObjectInScene(scene, resolvedChildId) ||
      findObjectInScene(scene, childId);
    const parentObj =
      findObjectInScene(scene, resolvedParentId) ||
      findObjectInScene(scene, parentId);
    const childAsset =
      childAssetByKey.get(resolvedChildId) ||
      childAssetByKey.get(childId) ||
      childAssetByKey.get(String(childId));

    if (childObj && parentObj) {
      if (!canEstablishParentChild(parentObj, childObj)) {
        return;
      }

      if (childObj.parent === parentObj) {
        applyStoredLocalTransform(childObj, childAsset);
        return;
      }

      // Prevent circular references
      let isCircular = false;
      let current = parentObj;
      while (current && current !== scene) {
        if (current === childObj) {
          isCircular = true;
          break;
        }
        current = current.parent;
      }

      if (!isCircular) {
        // Ensure matrices are fully updated before extracting world transforms
        parentObj.updateMatrixWorld(true);
        childObj.updateMatrixWorld(true);

        // Store world transform before reconstruction
        const worldPosition = childObj.getWorldPosition(new THREE.Vector3());
        const worldQuaternion = childObj.getWorldQuaternion(
          new THREE.Quaternion()
        );
        const worldScale = childObj.getWorldScale(new THREE.Vector3());

        // Remove child from current parent (use safe remove to avoid splice errors)
        if (childObj.parent && childObj.parent !== scene) {
          safeRemoveFromParent(childObj);
        }

        // Attach to new parent safely
        try {
          parentObj.add(childObj);
        } catch (error) {
          if (!parentObj.children) parentObj.children = [];
          if (!Object.isExtensible(parentObj.children)) parentObj.children = [...parentObj.children];
          parentObj.children.push(childObj);
          childObj.parent = parentObj;
        }

        if (!applyStoredLocalTransform(childObj, childAsset)) {
          // Convert world transform to local transform relative to new parent
          const parentWorldMatrixInv = parentObj.matrixWorld.clone().invert();
          const localPosition = new THREE.Vector3().copy(worldPosition).applyMatrix4(parentWorldMatrixInv);

          const parentWorldQuat = parentObj.getWorldQuaternion(new THREE.Quaternion());
          const localQuaternion = new THREE.Quaternion().copy(worldQuaternion).premultiply(parentWorldQuat.invert());

          const parentWorldScale = parentObj.getWorldScale(new THREE.Vector3());
          const localScale = new THREE.Vector3().copy(worldScale).divide(parentWorldScale);

          childObj.position.copy(localPosition);
          childObj.quaternion.copy(localQuaternion);

          // Avoid division by zero issues
          if (Math.abs(parentWorldScale.x) < 0.0001) localScale.x = 1;
          if (Math.abs(parentWorldScale.y) < 0.0001) localScale.y = 1;
          if (Math.abs(parentWorldScale.z) < 0.0001) localScale.z = 1;

          childObj.scale.copy(localScale);

          // Guard against NaN/Infinity from extreme parent-child scale ratios
          ['x', 'y', 'z'].forEach((axis) => {
            if (!Number.isFinite(childObj.scale[axis]) || childObj.scale[axis] === 0) {
              childObj.scale[axis] =
                childObj.deleteType === 'text' && axis === 'z' ? 0.000001 : 1;
            }
          });
          if (
            childObj.deleteType === 'text' &&
            Math.abs(childObj.scale.z) < 0.000001
          ) {
            childObj.scale.z = 0.000001;
          }
          if (!Number.isFinite(childObj.position.x)) childObj.position.set(0, 0, 0);
        }
        childObj.updateMatrixWorld();

        // Ensure proper naming for future lookups
        if (!childObj.name && childId) {
          childObj.name = childId;
        }
        if (!parentObj.name && parentId) {
          parentObj.name = parentId;
        }
      }
    }
  });

  enforceHotspotHierarchyRules(scene);

  scene.traverse((obj) => {
    if (obj.deleteType !== 'text') return;
    ['x', 'y', 'z'].forEach((axis) => {
      if (!Number.isFinite(obj.position[axis])) obj.position[axis] = 0;
      if (!Number.isFinite(obj.rotation[axis])) obj.rotation[axis] = 0;
      if (!Number.isFinite(obj.scale[axis]) || obj.scale[axis] === 0) {
        obj.scale[axis] = axis === 'z' ? 0.000001 : 1;
      }
    });
    if (Math.abs(obj.scale.z) < 0.000001) obj.scale.z = 0.000001;
    obj.updateMatrixWorld(true);
  });
};

const reconstructParentChildRelationshipsStandalone = reconstructParentChildRelationships;
export { reconstructParentChildRelationshipsStandalone };
